// What was read from the dec page, in the Record's words (spec C10, C16): the reading line in the
// Dec page step, the values to check, and the renewal premium for "Prior term vs renewal". Pure
// functions, so the step, the card and the tests read the same lines.
import { renewalPremiumOf as sharedPremiumOf } from '@safeside/shared';
import type { CheckReason, Reading, ReadingErrorKind, RenewalRow } from '../auth/api';
import { showValue } from './fix-lines';
import { longDate } from './renewal-lines';

const fieldLabels: Record<string, string> = {
  documentType: 'Document',
  namedInsured: 'Named insured',
  policyNumber: 'Policy #',
  effectiveDate: 'Term starts',
  expirationDate: 'Term ends',
  totalPremium: 'Renewal premium',
};

const reasonText: Record<CheckReason, string> = {
  low_confidence: 'not sure of the reading',
  not_found: 'not found on the PDF',
  policy_number_differs: 'not the policy number in AMS360',
  term_start_differs: 'not the day the current term ends in AMS360',
  term_length: 'not a 6 or 12 month term',
  premium_change: 'more than 25% from the prior premium',
  different_insured: 'not the insured in AMS360',
  not_a_dec_page: 'this does not look like a dec page',
  non_renewal: 'this is a non-renewal notice, not a dec page',
};

/** "Renewal premium: more than 25% from the prior premium" */
export const checkText = (c: { field: string; reason: CheckReason }) =>
  `${fieldLabels[c.field] ?? c.field}: ${reasonText[c.reason]}`;

const failedText: Record<ReadingErrorKind, string> = {
  not_readable: 'the file could not be opened',
  too_large: 'the file is too large to read',
  incomplete: 'the reading stopped early',
  service_error: 'the reading service did not answer',
  timeout: 'the reading took too long',
  not_configured: 'reading is not set up yet',
  error: 'something went wrong',
};

/** A reading still to finish: the Record asks again until it is done. */
export const readingOpen = (r: Reading | null) =>
  !!r && (r.status === 'queued' || r.status === 'running');

/** The renewal's reading or the prior term's (the coverage compare) still to finish. */
export const readingsPending = (r: Reading | null) =>
  readingOpen(r) || r?.prior?.status === 'queued' || r?.prior?.status === 'running';

/** The line under the dec page while it is read, or when it could not be read. */
export function readingLine(r: Reading): string {
  if (readingOpen(r)) return 'Reading the dec page';
  const v = r.values;
  if (r.status === 'failed') {
    const why = `Could not read this PDF (${failedText[r.errorKind ?? 'error']}).`;
    const typed = v ? valueParts(v) : [];
    return typed.length
      ? `${why} Typed from the PDF: ${typed.join(', ')}.`
      : `${why} Enter the values from the PDF under Prior term vs renewal.`;
  }
  if (!v) return 'Read from the dec page.';
  const corrected = r.fields?.some((f) => f.fix?.action === 'corrected');
  const parts = valueParts(v);
  const head = corrected ? 'From the dec page, as corrected' : 'Read from the dec page';
  return parts.length ? `${head}: ${parts.join(', ')}.` : `${head}.`;
}

function valueParts(v: NonNullable<Reading['values']>): string[] {
  const parts: string[] = [];
  if (v.totalPremium !== null) {
    parts.push(`renewal premium ${showValue('totalPremium', v.totalPremium)}`);
  }
  if (v.effectiveDate && v.expirationDate) {
    parts.push(`term ${longDate(v.effectiveDate)} to ${longDate(v.expirationDate)}`);
  }
  return parts;
}

/** The dec page may be another insured's: said first, in red (spec C16 "Wrong document"). */
export const otherInsured = (r: Reading | null) =>
  !!r && r.checks.some((c) => c.reason === 'different_insured');

/** "2 values to check" */
export const checksHeadline = (n: number) =>
  n === 1 ? '1 value to check' : `${n} values to check`;

/**
 * The renewal premium the Record uses (one rule everywhere, renewalPremiumOf in @safeside/shared):
 * the VA's correction, else the dec page's reading once it is read, else AMS360's renewed premium.
 * Worked out from the reading on the page, so a correction shows at once.
 */
export function renewalPremiumOf(
  r: Pick<RenewalRow, 'amsRenewalPremium'>,
  reading: Reading | null,
): { value: number; source: 'dec page' | 'AMS360' } | null {
  const settled = reading?.status === 'done' || reading?.status === 'failed';
  const f = settled ? reading.fields.find((x) => x.field === 'totalPremium') : undefined;
  const asNumber = (v: string | number | null | undefined) =>
    v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? null : Number(v);
  const p = sharedPremiumOf({
    corrected: f?.fix?.action === 'corrected' ? asNumber(f.value) : null,
    read: reading?.status === 'done' ? asNumber(f?.read) : null,
    ams: r.amsRenewalPremium,
  });
  if (!p) return null;
  return { value: p.value, source: p.source === 'ams360' ? 'AMS360' : 'dec page' };
}
